import type { HorizontalGuideSpan } from '@/components/app/document/overlays/guides-overlay'
import type { SmartGuideLine } from '@/lib/guides/smart-guides'

/**
 * Smart guides: the alignment/distribution lines and the rotation angle readout shown during a
 * move/resize/rotate drag.
 *
 * Migrated out of `SmartGuidesOverlay`, which drew each guide as a CSS `div` (`height`/`width: 1`
 * for the line, a `repeating-linear-gradient` for the rotation dash pattern) inside the zoom-scaled
 * canvas — so the "1px" line, and the dash period of the rotation guide, both grew with zoom exactly
 * like every other overlay fixed so far. Here the stroke is a real screen pixel via SVG
 * `strokeWidth`, and the dash pattern is an explicit `strokeDasharray` in screen units.
 *
 * `guides` arrives as a prop, not through the shape-manager poll: it is already `center-canvas`
 * React state (`activeSmartGuides`, fed by `onActiveSmartGuidesChange` from whichever manager is
 * running the current drag), and that drag already re-renders `center-canvas` every frame.
 *
 * One deliberate split from the line positions: a guide's `position`/`from`/`to` are real document
 * quantities (where an aligned edge actually is) and are projected by `scale` like any other
 * document coordinate. Everything else about the rotation guide is pure UI, not document geometry,
 * and legacy expressed all of it in document units, so all of it scaled with zoom along with the
 * line:
 *
 * - The radial line's *length* (a fixed 3000 document units) — grew absurd at high zoom, could fall
 *   short of the viewport at very low zoom.
 * - The angle readout's font size, padding, and its offset from the pivot (`left: cx + 14, top: cy -
 *   24` in the CSS version) — legacy positioned and sized the label in the same document-space div
 *   tree as the line, so all of those numbers were painted through the same ancestor `scale()`. At
 *   400% the label would render around 4× its intended size.
 *
 * Both are now fixed *screen* quantities, matching the treatment already given to the rotation
 * handle's own stem and label elsewhere in the migrated chrome — legible and identically sized at
 * any zoom, never scaling with the object being rotated.
 */

const GUIDE_COLOR = 'var(--doc-smart-guide-line)'
/** Screen px — a true constant stroke width, not a document measurement. */
const GUIDE_THICKNESS = 1
/** Screen px. Matches the CSS `repeating-linear-gradient(... 4px, transparent 4px ... 8px)` dash. */
const ROTATION_DASH = '4 4'
/** Screen px half-length of the rotation guide's radial line, each direction from the pivot. */
const ROTATION_LINE_HALF_LENGTH_PX = 1500
/** Screen px offset of the angle readout from the rotation pivot. */
const ROTATION_LABEL_OFFSET = { x: 14, y: -24 }

export type SmartGuidesLayerProps = {
  guides: SmartGuideLine[]
  /** Current canvas zoom. */
  scale: number
  /** When set, horizontal snap segments are clipped to this canvas X span (single-page). */
  horizontalClipSpan?: HorizontalGuideSpan | null
}

/** The alignment/distribution lines and the rotation guide's radial line — SVG, inside the shared `<svg>`. */
export function SmartGuidesLayer({ guides, scale, horizontalClipSpan }: SmartGuidesLayerProps) {
  if (guides.length === 0) return null

  return (
    <g data-smart-guides-layer="">
      {guides.map((guide, i) => {
        if (guide.type === 'rotation') {
          const cx = (guide.centerX ?? 0) * scale
          const cy = (guide.centerY ?? 0) * scale
          const rad = ((guide.angle ?? 0) * Math.PI) / 180
          // Local "up" rotated by the snapped angle — same convention as the chrome rotation stem.
          const dx = Math.sin(rad)
          const dy = -Math.cos(rad)

          return (
            <line
              key={`rotation-${i}`}
              x1={cx - dx * ROTATION_LINE_HALF_LENGTH_PX}
              y1={cy - dy * ROTATION_LINE_HALF_LENGTH_PX}
              x2={cx + dx * ROTATION_LINE_HALF_LENGTH_PX}
              y2={cy + dy * ROTATION_LINE_HALF_LENGTH_PX}
              stroke={GUIDE_COLOR}
              strokeWidth={GUIDE_THICKNESS}
              strokeDasharray={ROTATION_DASH}
              style={{ pointerEvents: 'none' }}
            />
          )
        }

        const isHorizontal = guide.type === 'horizontal'
        let { from, to } = guide

        if (horizontalClipSpan && isHorizontal) {
          const clipLeft = horizontalClipSpan.left
          const clipRight = horizontalClipSpan.left + horizontalClipSpan.width
          const clippedFrom = Math.max(guide.from, clipLeft)
          const clippedTo = Math.min(guide.to, clipRight)

          if (clippedTo <= clippedFrom) return null

          from = clippedFrom
          to = clippedTo
        }

        const position = guide.position * scale

        return (
          <line
            key={`${guide.type}-${i}`}
            x1={isHorizontal ? from * scale : position}
            y1={isHorizontal ? position : from * scale}
            x2={isHorizontal ? to * scale : position}
            y2={isHorizontal ? position : to * scale}
            stroke={GUIDE_COLOR}
            strokeWidth={GUIDE_THICKNESS}
            style={{ pointerEvents: 'none' }}
          />
        )
      })}
    </g>
  )
}

/**
 * The rotation guide's angle readout — HTML, not SVG.
 *
 * The pill auto-sizes to its text content ("5°" vs "120°") and needs padding and a border radius;
 * reproducing that in SVG would mean either measuring text width by hand or fixing the pill to a
 * width that is wrong for most angles. A plain HTML sibling keeps the same auto-sizing behaviour the
 * original CSS had, the same way the lock/freeze badge does.
 *
 * Anchored at a **fixed screen offset from the (projected) pivot** — `ROTATION_LABEL_OFFSET` is not
 * scaled, and does not track the far end of the radial line. That matches the legacy offset's actual
 * effect (`cx + 14, cy - 24`, a small constant distance from center, close to where the rotation
 * handle itself sits) even though it does not match the *appearance* of "attached to the line's tip"
 * the CSS version's positioning code might suggest — the line is 3000 units long in both versions,
 * far longer than the 14/24px offset, so the label was never actually near the line's far end.
 */
export function SmartGuidesRotationLabel({ guides, scale }: SmartGuidesLayerProps) {
  const rotation = guides.find((guide) => guide.type === 'rotation')

  if (!rotation) return null

  const pivotX = (rotation.centerX ?? 0) * scale
  const pivotY = (rotation.centerY ?? 0) * scale

  return (
    <div
      className="absolute flex items-center justify-center"
      style={{
        left: pivotX + ROTATION_LABEL_OFFSET.x,
        top: pivotY + ROTATION_LABEL_OFFSET.y,
        padding: '2px 6px',
        borderRadius: 3,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: GUIDE_COLOR,
        fontSize: 10,
        fontWeight: 600,
        fontFamily: 'ui-monospace, monospace',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
      }}
    >
      {Math.round(rotation.angle ?? 0)}°
    </div>
  )
}

export default SmartGuidesLayer
